import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth, usePermissions } from '../AuthContext';
import BrokerDrawer, { BrokerDoc } from '../components/BrokerDrawer';
import BrokerProfileReviewDialog from '../components/BrokerProfileReviewDialog';
import BrokerApprovalActions from '../components/BrokerApprovalActions';

function normalizeName(value: string) {
  return value.trim().replace(/\s+/g, ' ').toLowerCase();
}

export default function BrokersPage() {
  const { canAction } = usePermissions();
  const { roleId } = useAuth();
  const isAdmin = roleId === 'admin' || roleId === 'super-admin';
  const [searchParams, setSearchParams] = useSearchParams();
  const canAdd = canAction('brokers', 'addBroker');
  const canEdit = canAction('brokers', 'editBroker');
  const canDelete = canAction('brokers', 'deleteBroker');

  const [brokers, setBrokers] = useState<BrokerDoc[]>([]);
  const [filtered, setFiltered] = useState<BrokerDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<BrokerDoc | null>(null);
  const [profileBroker, setProfileBroker] = useState<BrokerDoc | null>(null);
  const [pendingProfiles, setPendingProfiles] = useState<Array<{
    id: string; brokerId: string; name: string; submittedAt: any;
  }>>([]);

  const [agencyFilter, setAgencyFilter] = useState<string>('');

  const brokersRef = useMemo(() => collection(db, 'brokers'), []);
  const brokerIdParam = useMemo(() => String(searchParams.get('brokerId') || '').trim(), [searchParams]);
  const pendingBrokerApprovals = useMemo(() => brokers.filter((broker) =>
    broker.approvalStatus === 'pending'), [brokers]);

  useEffect(() => {
    const q = query(brokersRef, orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const data = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as BrokerDoc[];
        setBrokers(data);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || 'Failed to load brokers.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [brokersRef]);

  useEffect(() => {
    const q = query(collection(db, 'brokerProfileSubmissions'), where('status', '==', 'pending'));
    return onSnapshot(q, (snap) => {
      setPendingProfiles(snap.docs.map((d) => {
        const data = d.data();
        return {id: d.id, brokerId: String(data.brokerId || ''),
          name: String(data.profile?.name || 'Broker'), submittedAt: data.submittedAt};
      }).sort((a, b) => (a.submittedAt?.toMillis?.() || 0) -
        (b.submittedAt?.toMillis?.() || 0)));
    }, (err) => setError(err.message));
  }, []);

  useEffect(() => {
    if (!brokerIdParam) return;
    const existing = brokers.find((b) => b.id === brokerIdParam) || null;
    if (existing) {
      setEditing(existing);
      setDrawerOpen(true);
      return;
    }
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'brokers', brokerIdParam));
        if (!snap.exists()) return;
        setEditing({ ...(snap.data() as BrokerDoc), id: snap.id });
        setDrawerOpen(true);
      } catch (err) {
        console.warn('Failed to open broker from URL', err);
      }
    })();
  }, [brokerIdParam, brokers]);

  const agencyOptions = useMemo(() => {
    const uniq = new Map<string, string>();
    for (const b of brokers) {
      if (!b.agencyName) continue;
      const canon = b.agencyName.trim().replace(/\s+/g, ' ');
      const norm = normalizeName(canon);
      if (!uniq.has(norm)) uniq.set(norm, canon);
    }
    return Array.from(uniq.values()).sort((a, b) => a.localeCompare(b));
  }, [brokers]);

  useEffect(() => {
    const norm = normalizeName(agencyFilter);
    if (!norm) {
      setFiltered(brokers);
      return;
    }
    setFiltered(
      brokers.filter((b) => {
        const bNorm = typeof (b as any).agencyNameLower === 'string'
          ? (b as any).agencyNameLower
          : normalizeName(b.agencyName || '');
        return bNorm === norm;
      })
    );
  }, [agencyFilter, brokers]);

  const handleOpenAdd = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleOpenEdit = (b: BrokerDoc) => {
    setEditing(b);
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
    if (brokerIdParam) {
      const next = new URLSearchParams(searchParams);
      next.delete('brokerId');
      setSearchParams(next, { replace: true });
    }
  };

  const handleDelete = async (b: BrokerDoc) => {
    const ok = window.confirm(`Delete broker "${b.displayName || b.brokerName}"?`);
    if (!ok) return;

    try {
      // Delete contacts subcollection first (small v1 cleanup).
      const batch = writeBatch(db);
      const contactsSnap = await getDocs(collection(db, 'brokers', b.id, 'contacts'));
      contactsSnap.docs.forEach((d) => batch.delete(d.ref));
      batch.delete(doc(db, 'brokers', b.id));
      await batch.commit();
    } catch (err: any) {
      setError(err?.message || 'Failed to delete broker.');
    }
  };

  return (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>
          Brokers
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            startIcon={<AddIcon />}
            variant="contained"
            onClick={handleOpenAdd}
            disabled={!canAdd}
          >
            New
          </Button>
          <Tooltip title="More">
            <IconButton>
              <MoreHorizIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {isAdmin && pendingBrokerApprovals.length > 0 && <Paper variant="outlined" sx={{p: 2, mb: 2, borderColor: '#d7aa67', bgcolor: '#fff8ed'}}>
        <Typography fontWeight={700} sx={{mb: 0.5}}>
          New brokers pending approval ({pendingBrokerApprovals.length})
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
          Check the name and mobile number before activating the broker or sending the profile form.
        </Typography>
        <Stack spacing={1.25}>
          {pendingBrokerApprovals.map((broker) => <Stack key={broker.id} direction={{xs: 'column', md: 'row'}}
            alignItems={{md: 'center'}} justifyContent="space-between" gap={1.25}
            sx={{p: 1.25, borderRadius: 1.5, bgcolor: '#fff', border: '1px solid #eadcc7'}}>
            <Stack spacing={0.25}>
              <Typography fontWeight={700}>{broker.brokerName || broker.displayName}</Typography>
              <Typography variant="body2" color="text.secondary">
                {broker.phoneE164 || broker.phoneNational} · Added by {broker.approvalRequestedBy || 'Front desk'}
              </Typography>
            </Stack>
            <BrokerApprovalActions brokerId={broker.id} />
          </Stack>)}
        </Stack>
      </Paper>}

      {isAdmin && pendingProfiles.length > 0 && <Paper variant="outlined" sx={{p: 2, mb: 2, borderColor: '#e5c99e', bgcolor: '#fff8ed'}}>
        <Typography fontWeight={700} sx={{mb: 1}}>
          Pending broker profile approval ({pendingProfiles.length})
        </Typography>
        <Stack spacing={1}>
          {pendingProfiles.map((item) => {
            const broker = brokers.find((b) => b.id === item.brokerId);
            return <Stack key={item.id} direction={{xs: 'column', sm: 'row'}}
              alignItems={{sm: 'center'}} justifyContent="space-between" gap={1}>
              <Typography>{item.name}{broker?.agencyName ? ` · ${broker.agencyName}` : ''}</Typography>
              <Button size="small" variant="outlined" disabled={!broker}
                onClick={() => broker && setProfileBroker(broker)}>Review profile</Button>
            </Stack>;
          })}
        </Stack>
      </Paper>}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }} alignItems={{ sm: 'center' }}>
        <Autocomplete
          sx={{ minWidth: { xs: '100%', sm: 360 } }}
          options={agencyOptions}
          value={agencyFilter || null}
          onChange={(_, v) => setAgencyFilter(v || '')}
          inputValue={agencyFilter}
          onInputChange={(_, v) => setAgencyFilter(v)}
          renderInput={(params) => (
            <TextField {...params} label="Filter by Agency" size="small" placeholder="Type agency name…" />
          )}
        />
        {agencyFilter && (
          <Button variant="text" onClick={() => setAgencyFilter('')}>
            Clear
          </Button>
        )}
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 0.125, boxShadow: 'none' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Broker Name</TableCell>
                <TableCell>Agency</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={(canEdit || canDelete) ? 7 : 6} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              )}
              {!loading && brokers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={(canEdit || canDelete) ? 7 : 6} align="center">
                    No brokers yet.
                  </TableCell>
                </TableRow>
              )}
              {!loading && brokers.length > 0 && filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={(canEdit || canDelete) ? 7 : 6} align="center">
                    No brokers match this agency.
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((b) => (
                <TableRow key={b.id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                      <Typography fontWeight={600}>{b.brokerName || '—'}</Typography>
                      {b.isAgencyHead ? (
                        <Chip size="small" color="primary" variant="outlined" label="Agency Head" />
                      ) : null}
                    </Stack>
                  </TableCell>
                  <TableCell>{b.agencyName || '—'}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2">
                        {b.phoneE164 ||
                          (b.phoneCountryCode && b.phoneNational
                            ? `${b.phoneCountryCode}${b.phoneNational}`
                            : '—')}
                      </Typography>
                      {b.phoneE164 && (
                        <Tooltip title="Call">
                          <IconButton
                            size="small"
                            component="a"
                            href={`tel:${b.phoneE164}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <PhoneOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={b.approvalStatus === 'pending' ? 'pending approval' : b.status || 'active'}
                      color={b.approvalStatus === 'pending' || b.status === 'inactive' ? 'default' : 'success'}
                      variant={b.approvalStatus === 'pending' || b.status === 'inactive' ? 'outlined' : 'filled'}
                    />
                  </TableCell>
                  <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" onClick={() => setProfileBroker(b)}>Profile & visits</Button>
                        {canEdit && (
                          <Tooltip title="Edit">
                            <span>
                              <IconButton size="small" onClick={() => handleOpenEdit(b)}>
                                <EditOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Tooltip title="Delete">
                            <span>
                              <IconButton size="small" color="error" onClick={() => handleDelete(b)}>
                                <DeleteOutlineOutlinedIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <BrokerDrawer
        open={drawerOpen}
        editing={editing}
        onClose={handleCloseDrawer}
        onSaved={() => {
          // no-op; realtime listener updates list
        }}
      />
      <BrokerProfileReviewDialog broker={profileBroker ?
        brokers.find((b) => b.id === profileBroker.id) || profileBroker : null}
      onClose={() => setProfileBroker(null)} />
    </>
  );
}
